import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { MessageSquare, Plus, Send, Copy, Trash2, Edit } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface MessageTemplate {
  id?: string;
  name: string;
  message_text: string;
  include_location: boolean;
  include_plan_excerpt: boolean;
}

interface QuickMessageTemplatesProps {
  user: any;
}

export const QuickMessageTemplates = ({ user }: QuickMessageTemplatesProps) => {
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<MessageTemplate | null>(null);
  const [newTemplate, setNewTemplate] = useState<MessageTemplate>({
    name: '',
    message_text: '',
    include_location: false,
    include_plan_excerpt: false
  });

  const defaultTemplates = [
    {
      name: "Quick Check-in",
      message_text: "Hi [name]. I'm struggling right now and could really use you. Can you call or message me? If you can, please call me on [phone].",
      include_location: false,
      include_plan_excerpt: false
    },
    {
      name: "Need Support",
      message_text: "Hey, I'm having a really hard time and need someone to talk to. Are you free to chat? I could use a friend right now.",
      include_location: false,
      include_plan_excerpt: false
    },
    {
      name: "Crisis Support",
      message_text: "Hi [name]. I'm having very difficult thoughts and I've put together a safety plan. I'd be so grateful if you could check in with me now. If I don't respond, please call emergency services. Thank you.",
      include_location: true,
      include_plan_excerpt: true
    },
    {
      name: "Just Checking In",
      message_text: "Hi there! Just wanted to reach out and see how you're doing. I'm thinking of you and hope you're well. Let me know if you want to chat sometime.",
      include_location: false,
      include_plan_excerpt: false
    }
  ];

  useEffect(() => {
    loadTemplates();
  }, [user?.id]);

  const loadTemplates = async () => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from('quick_message_templates')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTemplates(data || []);

      // If no templates exist, add default ones
      if (!data || data.length === 0) {
        await addDefaultTemplates();
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Failed to load message templates');
    } finally {
      setLoading(false);
    }
  };

  const addDefaultTemplates = async () => {
    try {
      const templatesWithUserId = defaultTemplates.map(template => ({
        ...template,
        user_id: user.id
      }));

      const { data, error } = await supabase
        .from('quick_message_templates')
        .insert(templatesWithUserId)
        .select();

      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      console.error('Error adding default templates:', error);
    }
  };

  const saveTemplate = async () => {
    if (!user?.id || !newTemplate.name.trim() || !newTemplate.message_text.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (editingTemplate?.id) {
        // Update existing template
        const { data, error } = await supabase
          .from('quick_message_templates')
          .update(newTemplate)
          .eq('id', editingTemplate.id)
          .select()
          .single();

        if (error) throw error;
        
        setTemplates(templates.map(t => t.id === editingTemplate.id ? data : t));
        toast.success('Template updated');
      } else {
        // Create new template
        const { data, error } = await supabase
          .from('quick_message_templates')
          .insert({
            ...newTemplate,
            user_id: user.id
          })
          .select()
          .single();

        if (error) throw error;
        
        setTemplates([data, ...templates]);
        toast.success('Template added');
      }

      setNewTemplate({
        name: '',
        message_text: '',
        include_location: false,
        include_plan_excerpt: false
      });
      setShowAddForm(false);
      setEditingTemplate(null);
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    }
  };

  const deleteTemplate = async (id: string) => {
    try {
      const { error } = await supabase
        .from('quick_message_templates')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setTemplates(templates.filter(t => t.id !== id));
      toast.success('Template deleted');
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  const copyMessage = async (template: MessageTemplate) => {
    let message = template.message_text;
    
    // Add location if requested
    if (template.include_location) {
      message += "\n\nMy approximate location: [Your current location]";
    }
    
    // Add safety plan excerpt if requested
    if (template.include_plan_excerpt) {
      message += "\n\nSafety Plan Excerpt: [Key contacts and coping strategies from my safety plan]";
    }

    try {
      await navigator.clipboard.writeText(message);
      toast.success('Message copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy message');
    }
  };

  const sendMessage = (template: MessageTemplate) => {
    let message = template.message_text;
    
    if (template.include_location) {
      message += "\n\nMy approximate location: [Your current location]";
    }
    
    if (template.include_plan_excerpt) {
      message += "\n\nSafety Plan Excerpt: [Key contacts and coping strategies from my safety plan]";
    }

    // Open SMS with pre-filled message
    const smsUrl = `sms:?body=${encodeURIComponent(message)}`;
    window.location.href = smsUrl;
    
    toast.success('Opening message app...');
  };

  const startEdit = (template: MessageTemplate) => {
    setEditingTemplate(template);
    setNewTemplate(template);
    setShowAddForm(true);
  };

  const cancelEdit = () => {
    setEditingTemplate(null);
    setNewTemplate({
      name: '',
      message_text: '',
      include_location: false,
      include_plan_excerpt: false
    });
    setShowAddForm(false);
  };

  if (loading) {
    return <div className="flex items-center justify-center p-8">Loading message templates...</div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-primary flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Quick Message Templates
            </CardTitle>
            <Button
              onClick={() => setShowAddForm(true)}
              className="bg-primary hover:bg-primary/90"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Template
            </Button>
          </div>
          <p className="text-muted-foreground">
            Pre-written messages to quickly reach out for support when you need it most.
          </p>
        </CardHeader>
      </Card>

      {/* Add/Edit Form */}
      {showAddForm && (
        <Card className="border-secondary/20 bg-secondary/10">
          <CardHeader>
            <CardTitle className="text-primary">
              {editingTemplate ? 'Edit Template' : 'Add New Template'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="template-name">Template Name</Label>
              <Input
                id="template-name"
                value={newTemplate.name}
                onChange={(e) => setNewTemplate({...newTemplate, name: e.target.value})}
                placeholder="e.g., Quick Check-in, Crisis Support"
              />
            </div>

            <div>
              <Label htmlFor="message-text">Message Text</Label>
              <Textarea
                id="message-text"
                value={newTemplate.message_text}
                onChange={(e) => setNewTemplate({...newTemplate, message_text: e.target.value})}
                placeholder="Write your message here... Use [name] and [phone] as placeholders."
                rows={4}
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="include-location">Include my location</Label>
                <Switch
                  id="include-location"
                  checked={newTemplate.include_location}
                  onCheckedChange={(checked) => setNewTemplate({...newTemplate, include_location: checked})}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label htmlFor="include-plan">Include safety plan excerpt</Label>
                <Switch
                  id="include-plan"
                  checked={newTemplate.include_plan_excerpt}
                  onCheckedChange={(checked) => setNewTemplate({...newTemplate, include_plan_excerpt: checked})}
                />
              </div>
            </div>

            <div className="flex gap-2">
              <Button onClick={saveTemplate} className="bg-secondary hover:bg-secondary/90">
                <Plus className="h-4 w-4 mr-2" />
                {editingTemplate ? 'Update Template' : 'Save Template'}
              </Button>
              <Button onClick={cancelEdit} variant="outline">
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Templates List */}
      <div className="space-y-4">
        {templates.length === 0 ? (
          <Card>
            <CardContent className="text-center py-8">
              <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                No message templates yet. Add some to quickly reach out for support.
              </p>
            </CardContent>
          </Card>
        ) : (
          templates.map((template) => (
            <Card key={template.id}>
              <CardContent className="p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-semibold text-lg">{template.name}</h3>
                    <div className="flex gap-2 mt-2">
                      {template.include_location && (
                        <Badge variant="outline" className="text-xs">Includes Location</Badge>
                      )}
                      {template.include_plan_excerpt && (
                        <Badge variant="outline" className="text-xs">Includes Safety Plan</Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => startEdit(template)}
                      size="sm"
                      variant="ghost"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      onClick={() => deleteTemplate(template.id!)}
                      size="sm"
                      variant="ghost"
                      className="text-destructive hover:text-destructive/80"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="bg-muted p-3 rounded text-sm mb-4">
                  {template.message_text}
                  {template.include_location && (
                    <div className="text-xs text-muted-foreground mt-2 italic">
                      + Location will be included
                    </div>
                  )}
                  {template.include_plan_excerpt && (
                    <div className="text-xs text-muted-foreground mt-1 italic">
                      + Safety plan excerpt will be included
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={() => sendMessage(template)}
                    size="sm"
                    className="bg-primary hover:bg-primary/90"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Send
                  </Button>
                  <Button
                    onClick={() => copyMessage(template)}
                    size="sm"
                    variant="outline"
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};